import { BaseLayer } from '.'
import type { Feature, FeatureCollection, MultiPoint, Point } from 'geojson'
import { apiFzu } from '@/apiFzu'
import qtIMG from '@/assets/bouyImg/其他.png?url' // 默认图标
import typeBankwiseL from '@/assets/bouyImg/white-coastal-marker.png?url'
import typeBankwiseR from '@/assets/bouyImg/red-coastal-marker.png?url'
import typeBuoyL from '@/assets/bouyImg/white-buoy2.png?url'
import typeBouyR from '@/assets/bouyImg/red-buoy2.png?url'
import typeCrossL from '@/assets/bouyImg/white-riverbank-marker1.png?url'
import typeCrossR from '@/assets/bouyImg/red-riverbank-marker.png?url'
import typeSideL from '@/assets/bouyImg/white-side-shoreline-marker1.png?url'
import typeSideR from '@/assets/bouyImg/red-side-shoreline-marker.png?url'
import typeSubmerged from '@/assets/bouyImg/pipeline-marking.png?url'
import typeYellowBuoy from '@/assets/bouyImg/dangerous-water-buoy.png?url'
import SGBImg from '@/assets/bouyImg/white-buoy2.png?url'
import MarkPopup from '../../views/Map/Popup/MarkPopup.vue'

import {
  Cartographic,
  Math,
  Color,
  DistanceDisplayCondition,
  NearFarScalar,
  BillboardGraphics,
  Entity,
  JulianDate,
  Cartesian2,
  LabelGraphics,
  LabelStyle,
  VerticalOrigin,
  HorizontalOrigin,
} from 'cesium'
import { Subscriber } from 'cesium-extends'

import { useMarkStore } from '@/stores/navimark'
import { useBridgeStore } from '@/stores/bridge'

// 定义航标类型到图标的映射
const iconMap: { [key: string]: { left: string; right: string } } = {
  'bankwise mark': {
    left: typeBankwiseL,
    right: typeBankwiseR,
  },
  Buoy: {
    left: typeBuoyL,
    right: typeBouyR,
  },
  'Cross River Mark': {
    left: typeCrossL,
    right: typeCrossR,
  },
  Sidemark: {
    left: typeSideL,
    right: typeSideR,
  },
}

// 特殊类型航标
const specialIconMap: { [key: string]: string } = {
  'Yellow Buoy': typeYellowBuoy,
  'Submerged Pipeline Shore Mark': typeSubmerged,
  'Stream Gauge Buoy': SGBImg,
}

export class BouyLayer extends BaseLayer<{
  click(p: any): void
}> {
  layername: string = '航标(矢量)'
  key: string = 'bouy'

  constructor() {
    super()
    this.once('mounted', (viewer) => {
      this._subscriber = new Subscriber(viewer, { pickResult: { enable: true } })
      this._subscriber.addExternal((_e, result) => {
        const entity = result?.id
        if (!(entity instanceof Entity) || !this.datasource.entities.contains(entity)) return
        const data = entity.properties?.getValue()
        const position = entity.position?.getValue()
        if (!position) return
        const cartographic = Cartographic.fromCartesian(position)
        const usermarkStore = useMarkStore()
        const userBridgeStore = useBridgeStore()
        const markID = entity.properties?.getValue().id
        this.popupCtrl.closeAll()
        userBridgeStore.closeDetail()
        usermarkStore.openMarkDetail(markID),
          this.popupCtrl.popup({
            viewer,
            id: entity.id,
            Com: MarkPopup as any,
            lnglat: [
              Math.toDegrees(cartographic.longitude),
              Math.toDegrees(cartographic.latitude),
            ] as [number, number],
            properties: data,
          })

        data && this.emit('click', data)
      }, 'LEFT_CLICK')
    })
  }

  private async loadGeojson(): Promise<FeatureCollection<Point>> {
    const res = await apiFzu.buoys.buoysControllerGetList()
    const featureCollection: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: [],
    }

    res.data.forEach((el) => {
      const geom = el.geom as unknown as Point
      if (!el.geom || !Array.isArray(geom.coordinates) || geom.coordinates.length !== 2) {
        console.warn('Skipping element due to invalid coordinates:', el)
        return
      }
      const [longitude, latitude] = geom.coordinates

      const feature: Feature<Point> = {
        type: 'Feature',
        id: el.id,
        geometry: { type: 'Point', coordinates: [longitude, latitude] },
        properties: el,
      }
      featureCollection.features.push(feature)
    })

    return featureCollection
  }

  async render() {
    const geojson = await this.loadGeojson()
    await this.datasource.load(geojson)
    this.datasource.entities.values.forEach((entity) => {
      this.renderEntity(entity)
    })
  }

  private renderEntity(entity: Entity) {
    const properties = entity.properties?.getValue(JulianDate.now())
    if (!properties) return

    let image = qtIMG // 默认图标
    const name = properties.name || ''
    const type = properties.type || '' // 获取 type 字段

    // 从 type 字段中提取基本类型和左右信息
    let baseType = ''
    let isLeft = false
    let isRight = false

    // 匹配所有可能的类型
    for (const typeKey of Object.keys(iconMap)) {
      if (type.includes(typeKey)) {
        baseType = typeKey
        break
      }
    }

    // 判断左右
    isLeft = type.includes('Left')
    isRight = type.includes('Right')

    // 选择图标
    if (type in specialIconMap) {
      image = specialIconMap[type]
    } else if (baseType && (isLeft || isRight)) {
      image = isLeft ? iconMap[baseType].left : iconMap[baseType].right
    }

    // 定义图标的显示距离条件 - 远距离可见
    const iconDisplayCondition = new DistanceDisplayCondition(0, 30000)
    // 定义标签的显示距离条件 - 只在近距离可见
    const labelDisplayCondition = new DistanceDisplayCondition(0, 10000)

    const scaleDistance = new NearFarScalar(1e3, 1, 3e4, 0.2)

    // 设置图标
    entity.billboard = new BillboardGraphics({
      image: image,
      disableDepthTestDistance: 3000000,
      scaleByDistance: scaleDistance,
      distanceDisplayCondition: iconDisplayCondition,
      verticalOrigin: VerticalOrigin.CENTER,
      scale: 1.3,
    })

    // 添加标签
    entity.label = new LabelGraphics({
      text: name,
      font: '16px sans-serif',
      style: LabelStyle.FILL_AND_OUTLINE,
      outlineWidth: 3,
      outlineColor: Color.BLACK,
      fillColor: Color.WHITE,
      verticalOrigin: VerticalOrigin.BOTTOM,
      horizontalOrigin: HorizontalOrigin.CENTER,
      pixelOffset: new Cartesian2(0, -20), // 向上偏移，避免遮挡图标
      distanceDisplayCondition: labelDisplayCondition,
      scaleByDistance: new NearFarScalar(1e3, 0.8, 1e4, 0.5),
      disableDepthTestDistance: 3000000,
      backgroundPadding: new Cartesian2(7, 5),
      backgroundColor: new Color(0, 0, 0, 0.6),
      showBackground: true,
    })
  }
}
